import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  LuCake,
  LuClover,
  LuUser,
  LuLayoutTemplate,
  LuCalendar,
  LuCircleAlert,
  LuHash,
  LuClipboardCheck
} from 'react-icons/lu';
import { useAuth } from '../contexts/AuthContext';
import { formatSituacaoContrato, resolveSituacaoRaw } from '../utils/situacaoContrato';

const ContractInfoContainer = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
`;


const ContractDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ContractItem = styled.div`
  background: #f8fafc;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    padding: 14px;
  }
`;

const ContractItemLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.grayMedium};
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 5px;
`;

const ContractItemValue = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
`;

const getStatusTone = (statusValue) => {
  const status = (statusValue || '').toString().toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (status === 'atrasado' || status === 'em atraso' || status === 'inativo') {
    return { gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' };
  }

  if (status === 'pendente') {
    return { gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' };
  }

  // Adimplente, Pré cadastro, Em dia, Ativo, vazio tratado como em dia
  if (
    !status ||
    status === 'ativo' ||
    status === 'em dia' ||
    status === 'adimplente' ||
    status.includes('pre cadastro') ||
    status.includes('precadastro')
  ) {
    return { gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' };
  }

  return { gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)' };
};

const StatusContractItem = styled(ContractItem)``;

const StatusBadgeWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 4px;
`;

const StatusHint = styled.p`
  margin: 0;
  font-size: 10px;
  line-height: 1.3;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 500;
`;

// Componente animado para o Nº da Sorte
const LuckyNumberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%);
  border-radius: 10px;
  padding: 8px 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(29, 78, 216, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%);
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

// Fallback para navegadores sem suporte a @property
const LuckyNumberDisplayFallback = styled.div`
  font: 800 24px system-ui;
  color: white;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
`;

const LuckyNumberLabel = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.grayMedium};
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LuckyNumberHint = styled.p`
  margin: 6px 0 0;
  font-size: 10px;
  line-height: 1.3;
  color: ${props => props.theme.colors.grayMedium};
  text-align: center;
  font-weight: 500;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  background: ${props => props.$tone?.gradient || '#64748b'};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

// Birthday Card (inspirado no card_niver.html)
const BirthdayCard = styled.div`
  position: relative;
  border-radius: 22px;
  padding: 24px;
  margin: 8px 0 20px;
  background: linear-gradient(160deg, #0f172a, #1e293b);
  color: #e5e7eb;
  box-shadow: 0 10px 30px rgba(0,0,0,.25), 0 0 0 1px rgba(255,255,255,.06) inset;
  border: 1px solid rgba(255,255,255,.12);
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 18px;
    margin: 6px 0 16px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 16px;
    margin: 4px 0 12px;
  }
`;

const BirthdayGlow = styled.div`
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(135deg, #ff9ec4, #b39eff, #7EE081);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: .5;
  pointer-events: none;
`;

const BirthdayRibbon = styled.div`
  position: absolute;
  right: 0;
  top: 14px;
  transform: translateX(14px) rotate(2deg);
  background: linear-gradient(90deg, #ff4d6d, #ff9ec4);
  color: #fff;
  padding: 8px 18px;
  font-weight: 700;
  letter-spacing: .5px;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
  box-shadow: 0 6px 16px rgba(255,77,109,.35);
  
  @media (max-width: 768px) {
    top: 12px;
    padding: 6px 14px;
    font-size: 14px;
    transform: translateX(12px) rotate(2deg);
  }
  
  @media (max-width: 480px) {
    top: 10px;
    padding: 5px 12px;
    font-size: 12px;
    transform: translateX(10px) rotate(1deg);
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
`;

const BirthdayHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    flex-direction: column;
    align-items: flex-start;
    text-align: center;
    padding-top: 20px;
  }
`;

const BirthdayBadge = styled.div`
  display: grid;
  place-items: center;
  height: 56px;
  width: 56px;
  flex: 0 0 56px;
  border-radius: 16px;
  background: radial-gradient(circle at 30% 30%, #ffffffaa, #ffffff11 55%), linear-gradient(135deg, #ffc8dd, #b39eff);
  color: #1f2937;
  font-size: 28px;
  box-shadow: inset 0 -2px 8px #0003;
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
  }
  
  @media (max-width: 768px) {
    height: 48px;
    width: 48px;
    flex: 0 0 48px;
    font-size: 24px;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    height: 44px;
    width: 44px;
    flex: 0 0 44px;
    font-size: 22px;
    border-radius: 12px;
    align-self: center;
  }
`;

const BirthdayTitleWrap = styled.div`
  line-height: 1.2;
`;

const BirthdayTitle = styled.h4`
  margin: 0;
  font-size: clamp(1.1rem, 1.8vw + .6rem, 1.5rem);
  color: #e5e7eb;
  
  @media (max-width: 768px) {
    font-size: clamp(1rem, 1.6vw + .5rem, 1.3rem);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(0.9rem, 1.4vw + .4rem, 1.1rem);
    text-align: center;
    width: 100%;
  }
`;

const BirthdaySub = styled.p`
  margin: 4px 0 0;
  color: #cbd5e1;
  font-size: .95rem;
  
  @media (max-width: 768px) {
    font-size: .9rem;
  }
  
  @media (max-width: 480px) {
    font-size: .85rem;
    text-align: center;
    width: 100%;
  }
`;

const BirthdayBody = styled.div`
  margin-top: 16px;
  line-height: 1.6;
  color: #e2e8f0;
  font-size: 1.02rem;
  
  @media (max-width: 768px) {
    margin-top: 14px;
    font-size: .98rem;
  }
  
  @media (max-width: 480px) {
    margin-top: 12px;
    font-size: .92rem;
    line-height: 1.5;
  }
`;

const BirthdayCta = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    margin-top: 16px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    margin-top: 14px;
    gap: 8px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const Coupon = styled.code`
  user-select: all;
  display: inline-grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #0b1220;
  border: 1px dashed #94a3b8;
  color: #c7d2fe;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    padding: 8px 10px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    font-size: 13px;
    text-align: center;
    justify-content: center;
    width: 100%;
  }
`;

const BirthdayButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 14px;
  font-weight: 700;
  background: linear-gradient(135deg, #7ee081, #2dd4bf);
  color: #0b1220;
  box-shadow: 0 6px 16px rgba(45, 212, 191,.32);
  transition: transform .12s ease, box-shadow .12s ease;
  &:hover{ transform: translateY(-1px); box-shadow: 0 10px 18px rgba(45, 212, 191,.36) }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 14px;
    width: 100%;
  }
`;

const BirthdayButtonAlt = styled(BirthdayButton)`
  background: linear-gradient(135deg, #f0abfc, #93c5fd);
  box-shadow: 0 6px 16px rgba(147, 197, 253,.28);
`;

const Balloon = styled.div`
  position: absolute;
  right: -30px;
  bottom: -30px;
  width: 200px;
  height: 200px;
  opacity: .35;
  z-index: -1;
  &::before, &::after{
    content: '';
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, #fff7, transparent 60%),
                radial-gradient(circle at 70% 20%, #fff4, transparent 40%),
                #7b2cbf;
    width: 100px;
    height: 120px;
    left: 20px;
    top: 20px;
    box-shadow: 70px 40px 0 0 #ff4d6d;
    filter: blur(.2px);
  }
  i{
    position: absolute;
    left: 65px;
    top: 130px;
    height: 60px;
    width: 1px;
    background: #fff8;
    box-shadow: 70px 40px 0 0 #fff8;
  }
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
    right: -20px;
    bottom: -20px;
    opacity: .25;
  }
  
  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
    right: -15px;
    bottom: -15px;
    opacity: .2;
  }
`;

// Componente animado para o Nº da Sorte
const AnimatedLuckyNumber = ({ finalValue, hasValidNumber }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (hasValidNumber && finalValue > 0) {
      // Iniciar animação apenas se houver número válido
      setIsAnimating(true);
      setDisplayValue(0);

      const duration = 3000; // 3 segundos
      const steps = 60; // 60 frames
      const increment = finalValue / steps;
      const stepDuration = duration / steps;

      let currentStep = 0;

      intervalRef.current = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.floor(increment * currentStep), finalValue);
        setDisplayValue(newValue);

        if (currentStep >= steps) {
          clearInterval(intervalRef.current);
          setIsAnimating(false);
        }
      }, stepDuration);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [finalValue, hasValidNumber]);

  // Se não há número válido, mostrar apenas "---"
  if (!hasValidNumber) {
    return (
      <LuckyNumberDisplayFallback>
        - - -
      </LuckyNumberDisplayFallback>
    );
  }

  return (
    <LuckyNumberDisplayFallback>
      {isAnimating ? displayValue : finalValue}
    </LuckyNumberDisplayFallback>
  );
};

const ContractInfo = ({ contrato, cliente }) => {
  const { selectedContrato, contratos } = useAuth();
  const statusContrato = formatSituacaoContrato(
    resolveSituacaoRaw(contrato, selectedContrato, contratos)
  );
  if (!contrato) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    let parsed;
    if (date instanceof Date) {
      parsed = date;
    } else if (typeof date === 'string') {
      // Se já estiver em DD/MM/AAAA, fazer parsing manual
      const ddmmyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
      if (ddmmyyyy.test(date)) {
        const [dayStr, monthStr, yearStr] = date.split('/');
        const day = parseInt(dayStr, 10);
        const month = parseInt(monthStr, 10) - 1;
        const year = parseInt(yearStr, 10);
        parsed = new Date(year, month, day);
      } else if (date.includes('-')) {
        // Se for formato ISO (YYYY-MM-DD), fazer parsing manual para evitar timezone
        const datePart = date.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        parsed = new Date(year, month - 1, day);
      } else {
        parsed = new Date(date);
      }
    } else if (typeof date === 'number') {
      parsed = new Date(date);
    }
    if (!parsed || isNaN(parsed.getTime())) return 'N/A';
    return new Intl.DateTimeFormat('pt-BR').format(parsed);
  };

  // Datas de aniversário (titular)
  const parseDateFlexible = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const ddmmyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
      if (ddmmyyyy.test(value)) {
        const [d, m, y] = value.split('/').map(v => parseInt(v, 10));
        return new Date(y, m - 1, d);
      }
      // Se for formato ISO (YYYY-MM-DD), fazer parsing manual
      if (value.includes('-')) {
        const datePart = value.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const tryDate = new Date(value);
      return isNaN(tryDate.getTime()) ? null : tryDate;
    }
    if (typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const getMonthNamePt = (monthIndex) => {
    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    return monthIndex >= 0 && monthIndex < 12 ? meses[monthIndex] : '';
  };

  const getMonthCodePt = (monthIndex) => {
    const codes = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
    return monthIndex >= 0 && monthIndex < 12 ? codes[monthIndex] : '';
  };

  const birthDate = parseDateFlexible(cliente?.dataNascimento);
  const isBirthdayMonth = (() => {
    if (!birthDate) return false;
    const now = new Date();
    return now.getMonth() === birthDate.getMonth();
  })();
  const birthdayMonthName = birthDate ? getMonthNamePt(birthDate.getMonth()) : '';
  const couponCode = birthDate ? `ANIVER15-${getMonthCodePt(birthDate.getMonth())}` : '';

  // Função para converter o número da sorte em valor numérico
  const getLuckyNumberValue = (numeroDaSorte) => {
    if (!numeroDaSorte || numeroDaSorte === '---') return 0;
    // Remove caracteres não numéricos e converte para número
    const cleanNumber = numeroDaSorte;
    return parseInt(cleanNumber) || 0;
  };

  // Função para verificar se há um número válido
  const hasValidLuckyNumber = (numeroDaSorte) => {
    if (!numeroDaSorte || numeroDaSorte === '---' || numeroDaSorte === 'N/A') return false;
    const cleanNumber = numeroDaSorte;
    return parseInt(cleanNumber) > 0;
  };

  const luckyNumberValue = getLuckyNumberValue(contrato.numeroDaSorte);
  const hasValidNumber = hasValidLuckyNumber(contrato.numeroDaSorte);
  const statusTone = getStatusTone(statusContrato);

  const scrollToPayments = () => {
    try {
      const element = document.getElementById('historico-pagamentos');
      if (element && element.scrollIntoView) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (e) {
      // noop
    }
  };

  return (
    <ContractInfoContainer>
      {isBirthdayMonth && (
        <BirthdayCard role="region" aria-label="Mês de Aniversário">
          <BirthdayGlow />
          <BirthdayRibbon>
            Mês de Aniversário 🎂
          </BirthdayRibbon>
          <BirthdayHead>
            <BirthdayBadge aria-hidden="true">
              <LuCake />
            </BirthdayBadge>
            <BirthdayTitleWrap>
              <BirthdayTitle>Parabéns, {cliente?.nome || 'Cliente'}! 🥳</BirthdayTitle>
              <BirthdaySub>Estamos celebrando seu mês de aniversário — <strong>{birthdayMonthName}</strong>.</BirthdaySub>
            </BirthdayTitleWrap>
          </BirthdayHead>
          <Balloon aria-hidden="true"><i /></Balloon>
        </BirthdayCard>
      )}

      <ContractDetails>
        <ContractItem>
          <ContractItemLabel>
            <LuHash size={12} /> Número do Contrato
          </ContractItemLabel>
          <ContractItemValue>{contrato.numero || 'N/A'}</ContractItemValue>
        </ContractItem>

        <ContractItem>
          <ContractItemLabel>
            <LuClipboardCheck size={12} /> Plano
          </ContractItemLabel>
          <ContractItemValue>{contrato.plano || 'N/A'}</ContractItemValue>
        </ContractItem>

        <ContractItem>
          <ContractItemLabel>
            <LuCalendar size={12} /> Data de Início
          </ContractItemLabel>
          <ContractItemValue>{formatDate(contrato.inicioVigencia)}</ContractItemValue>
        </ContractItem>

        <ContractItem>
          <ContractItemLabel>
            <LuCalendar size={12} /> Próximo Vencimento
          </ContractItemLabel>
          <ContractItemValue>{formatDate(contrato.proximoVencimento)}</ContractItemValue>
        </ContractItem>

        <ContractItem>
          <LuckyNumberLabel>
            <LuClover size={12} /> Nº da Sorte
          </LuckyNumberLabel>
          <LuckyNumberContainer>
            <AnimatedLuckyNumber finalValue={luckyNumberValue} hasValidNumber={hasValidNumber} />
          </LuckyNumberContainer>
          <LuckyNumberHint>Nº dinâmico · atualizado por sorteio</LuckyNumberHint>
        </ContractItem>

        <StatusContractItem>
          <ContractItemLabel>
            <LuCircleAlert size={12} /> Status do Contrato
          </ContractItemLabel>
          <StatusBadgeWrap>
            <StatusBadge
              $tone={statusTone}
              onClick={scrollToPayments}
              title="Ver histórico de pagamentos"
            >
              {statusContrato}
            </StatusBadge>
            <StatusHint>
              Clique para ver o histórico de pagamentos
            </StatusHint>
          </StatusBadgeWrap>
        </StatusContractItem>
      </ContractDetails>
    </ContractInfoContainer>
  );
};

export default ContractInfo;


